import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, CheckCircle, Clock, AlertTriangle, Download, Upload, Eye, Trash2, ExternalLink, FileText, Award, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const EPREUVES_LABELS = {
  'valide': { text: 'Acquis (1er passage)', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'rattrapage_valide': { text: 'Acquis (rattrapage)', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  'rattrapage': { text: 'Non acquis', color: 'bg-red-100 text-red-700 border-red-200' },
  'non_valide': { text: 'Non acquis', color: 'bg-red-100 text-red-700 border-red-200' },
};

const EPMSPSectionStagiaire = ({ user }) => {
  const [epmspData, setEpmspData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadData();
  }, [user.user_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [epmspRes, docsRes] = await Promise.all([
        axios.get(`/epmsp/stagiaire/${user.user_id}`).catch(() => ({ data: null })),
        axios.get(`/documents/${user.user_id}`).catch(() => ({ data: [] }))
      ]);
      setEpmspData(epmspRes.data);
      setDocuments(docsRes.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificat = async () => {
    try {
      const res = await axios.get(`/epmsp/certificat/${user.user_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${user.nom || 'stagiaire'}_${user.prenom || ''}_certificat_EPMSP.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificat EPMSP téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement du certificat');
    }
  };

  const handleUploadDeclaration = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'declaration_epmsp');
      formData.append('user_id', user.user_id);
      await axios.post('/upload', formData);
      toast.success('Déclaration stagiaire déposée');
      loadData();
    } catch {
      toast.error('Erreur lors du dépôt');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleViewDoc = async (docId) => {
    try {
      const res = await axios.get(`/documents/download/${docId}`);
      if (res.data?.file_data) {
        const byteChars = atob(res.data.file_data);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: res.data.content_type || 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch {
      toast.error('Impossible de charger le document');
    }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`/documents/download/${docId}`);
      if (res.data?.file_data) {
        const byteChars = atob(res.data.file_data);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: res.data.content_type || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${docId}`);
      toast.success('Document supprimé');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="epmsp-section-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="h-4 bg-slate-100 rounded w-2/3"></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="h-12 bg-slate-100 rounded"></div>
            <div className="h-12 bg-slate-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const epreuves = epmspData?.epreuves || {};
  const allValidated = epmspData?.all_validated || false;
  const validatedCount = epmspData?.validated_count || 0;
  const totalEpreuves = epmspData?.total_epreuves || 4;
  const declaration = documents.find(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp');
  const hasEpreuves = Object.keys(epreuves).length > 0;

  // Si aucune donnée EPMSP, ne rien afficher
  if (!hasEpreuves && !allValidated) return null;

  return (
    <div className={`border rounded-xl shadow-sm overflow-hidden ${
      allValidated ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200' : 'bg-white border-slate-200'
    }`} data-testid="epmsp-section-stagiaire">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
        data-testid="epmsp-section-toggle"
      >
        <div className={`p-2.5 rounded-xl ${allValidated ? 'bg-emerald-100' : 'bg-slate-100'}`}>
          <Shield className={`h-6 w-6 ${allValidated ? 'text-emerald-600' : 'text-slate-500'}`} />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-800">Épreuves EPMSP</h2>
          <p className="text-xs text-slate-500">Exigences Préalables à la Mise en Situation Professionnelle</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress */}
          <div className="flex items-center gap-2">
            <div className="w-24 h-2.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${allValidated ? 'bg-emerald-500' : 'bg-orange-400'}`}
                style={{ width: `${(validatedCount / totalEpreuves) * 100}%` }}
              />
            </div>
            <span className={`text-sm font-bold ${allValidated ? 'text-emerald-600' : 'text-slate-600'}`}>
              {validatedCount}/{totalEpreuves}
            </span>
          </div>
          {allValidated && (
            <Badge className="bg-emerald-500 text-white border-0 text-xs">ACQUIS</Badge>
          )}
          {!allValidated && validatedCount > 0 && (
            <Badge className="bg-orange-500 text-white border-0 text-xs">RATTRAPAGE</Badge>
          )}
          {!allValidated && validatedCount === 0 && hasEpreuves && (
            <Badge className="bg-red-500 text-white border-0 text-xs">NON ACQUIS</Badge>
          )}
          {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4">
          {/* Résultats par épreuve */}
          <div className="space-y-2" data-testid="epmsp-epreuves-list">
            {Object.entries(epreuves).map(([nom, ep]) => {
              const statusInfo = EPREUVES_LABELS[ep.statut] || { text: 'En attente', color: 'bg-slate-100 text-slate-500 border-slate-200' };
              const isValidated = ep.statut === 'valide' || ep.statut === 'rattrapage_valide';
              const isRattrapage = ep.statut === 'rattrapage_valide';

              return (
                <div key={nom} className={`rounded-lg border p-3.5 ${
                  isValidated ? 'bg-white border-emerald-200' : ep.statut === 'rattrapage' ? 'bg-red-50/50 border-red-200' : 'bg-white border-slate-200'
                }`} data-testid={`epreuve-item-${nom.substring(0, 15)}`}>
                  <div className="flex items-start gap-3">
                    {isValidated
                      ? <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                      : ep.statut === 'rattrapage'
                      ? <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                      : <Clock className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm text-slate-800">{nom}</p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${statusInfo.color}`}>
                          {statusInfo.text}
                        </span>
                      </div>

                      {/* Détails : date, évaluateur, commentaire */}
                      <div className="mt-1.5 space-y-1">
                        {ep.date_epreuve && (
                          <p className="text-xs text-slate-500">
                            Évalué le {new Date(ep.date_epreuve).toLocaleDateString('fr-FR')}
                            {ep.validated_by_name && <span> par <strong>{ep.validated_by_name}</strong></span>}
                          </p>
                        )}

                        {/* Commentaire */}
                        {ep.commentaire && (
                          <div className="flex items-start gap-1.5 text-xs text-slate-600 bg-slate-50 p-1.5 rounded">
                            <MessageSquare className="h-3 w-3 mt-0.5 shrink-0 text-slate-400" />
                            <span className="italic">"{ep.commentaire}"</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Épreuves non encore évaluées */}
            {totalEpreuves > Object.keys(epreuves).length && (
              <p className="text-xs text-slate-400 italic pl-2">
                {totalEpreuves - Object.keys(epreuves).length} épreuve(s) en attente d'évaluation
              </p>
            )}
          </div>

          {/* Résultat final */}
          {allValidated && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="epmsp-result-final">
              <div className="flex items-center gap-3">
                <Award className="h-6 w-6 text-emerald-600" />
                <div>
                  <p className="font-bold text-emerald-800">Résultat définitif : ACQUIS</p>
                  <p className="text-xs text-emerald-600">Toutes les épreuves EPMSP ont été validées avec succès.</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions post-validation */}
          {allValidated && (
            <div className="space-y-3" data-testid="epmsp-post-validation">
              {/* Étape 1: Télécharger certificat */}
              <div className="bg-white rounded-lg border border-emerald-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-slate-800">Télécharger votre certificat EPMSP</p>
                    <p className="text-xs text-slate-500 mb-2">
                      Téléchargez le certificat officiel pour effectuer votre déclaration d'éducateur sportif.
                    </p>
                    <Button size="sm" onClick={handleDownloadCertificat} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="download-certificat-epmsp-btn">
                      <Download className="h-3.5 w-3.5 mr-1" /> Télécharger le certificat
                    </Button>
                  </div>
                </div>
              </div>

              {/* Étape 2: Se déclarer en ligne */}
              <div className="bg-white rounded-lg border border-blue-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-slate-800">Effectuer votre déclaration en ligne</p>
                    <p className="text-xs text-slate-500 mb-2">
                      Rendez-vous sur le site officiel pour vous déclarer en tant qu'éducateur sportif.
                    </p>
                    <a
                      href="https://declaration-educateur.sports.gouv.fr/authentification"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
                      data-testid="link-declaration-educateur"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Accéder au site de déclaration
                    </a>
                  </div>
                </div>
              </div>

              {/* Étape 3: Déposer la déclaration */}
              <div className={`bg-white rounded-lg border p-4 ${declaration ? 'border-emerald-200' : 'border-amber-200'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    declaration ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
                  }`}>3</div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-slate-800">
                      {declaration ? 'Déclaration stagiaire déposée' : 'Déposer votre déclaration stagiaire'}
                    </p>
                    <p className="text-xs text-slate-500 mb-2">
                      {declaration
                        ? "Votre déclaration a été déposée. L'entreprise, le tuteur et le centre ont été notifiés."
                        : "Après avoir effectué votre déclaration, déposez ici le document signé."
                      }
                    </p>

                    {declaration && (
                      <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded border border-emerald-200 mb-2">
                        <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-emerald-700 truncate">{declaration.filename}</p>
                          {declaration.uploaded_at && (
                            <p className="text-xs text-emerald-600">Déposé le {new Date(declaration.uploaded_at).toLocaleDateString('fr-FR')}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDoc(declaration.document_id)} data-testid="view-declaration-epmsp">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc(declaration.document_id, declaration.filename)} data-testid="download-declaration-epmsp">
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDoc(declaration.document_id)} data-testid="delete-declaration-epmsp">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="relative inline-block">
                      <input
                        type="file"
                        onChange={handleUploadDeclaration}
                        disabled={uploading}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        data-testid="upload-input-declaration-epmsp"
                      />
                      <Button
                        size="sm"
                        className={`pointer-events-none ${
                          declaration ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'
                        } text-white`}
                        disabled={uploading}
                        data-testid="upload-btn-declaration-epmsp"
                      >
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        {uploading ? 'Upload en cours...' : declaration ? 'Remplacer' : 'Déposer la déclaration'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EPMSPSectionStagiaire;
